// i18next bootstrap for the admin SPA (i18n S4.1).
//
// Two layers of localisation live side by side:
//   1. UI strings — static interface text, translated here via react-i18next
//      with JSON catalogues bundled at build time (src/locales/*.json).
//   2. Content names — stage/operation/flow display names resolved by the
//      backend per request locale.  The chosen locale is mirrored to every
//      API call through the `X-Locale` header (see lib/api.ts), so switching
//      language re-fetches localised content.
//
// The single source of truth for "the user's chosen language" is the
// localStorage key below.  i18next reads it on init; the switcher writes it
// and calls setLocale() which flips i18next AND notifies the app so
// react-query refetches content under the new X-Locale.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "@/locales/en.json";
import ru from "@/locales/ru.json";

/** UI languages that ship with a complete catalogue.  Content locales may be
 *  broader (any locale in the translations catalogue, e.g. zh) — those still
 *  resolve server-side; the UI just falls back to the default language. */
export const UI_LOCALES = ["ru", "en"] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

export const DEFAULT_LOCALE: UiLocale = "ru";

const STORAGE_KEY = "mes:locale";

/** Read the persisted locale, falling back to the browser hint, then ru. */
export function readStoredLocale(): string {
  if (typeof localStorage !== "undefined") {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) return v;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const base = navigator.language.split("-")[0]?.toLowerCase();
    if (base) return base;
  }
  return DEFAULT_LOCALE;
}

/** Current locale string sent to the backend as X-Locale. May be any locale
 *  (incl. content-only ones); UI rendering falls back via i18next. */
export function getLocale(): string {
  return readStoredLocale();
}

/** Persist + apply a new locale.  Flips i18next language (UI strings) and
 *  dispatches `mes:locale-change` so AppShell can invalidate content queries
 *  that depend on the X-Locale header. */
export function setLocale(locale: string): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, locale);
  }
  void i18n.changeLanguage(locale);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("mes:locale-change"));
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    ru: { translation: ru },
    en: { translation: en },
  },
  lng: readStoredLocale(),
  fallbackLng: DEFAULT_LOCALE,
  // Content-only locales (zh, …) have no UI catalogue — fall back silently.
  supportedLngs: false,
  nonExplicitSupportedLngs: true,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

export default i18n;
